/**
 * AI spam filter hooks — Vault-backed, account-scoped.
 *
 * Settings toggle + custom rules, the Report-Spam / Not-Spam message actions,
 * and whitelist/blacklist management. Mirrors useScreening.ts conventions.
 */

import { useCallback, useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { ToastAction } from '@/components/ui/toast';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';
import type {
  EmailSpamBlockedSender,
  EmailSpamMovedMessage,
  EmailSpamReportTarget,
  EmailSpamReportResult,
  EmailSenderTrashedMail,
  EmailKnownSender,
  MailboxSpamSettingsDto,
  MailboxSpamRetentionDto,
  EmailSpamSpendDto,
} from '@/lib/api';

const mb = queryKeys.email.mailbox;
const conn = queryKeys.email.connections;

/** Guard against a server that keeps answering `capped`: 20 × 5000 = 100k rows. */
const MAX_REPORT_ROUNDS = 20;
/** Undo per request: messages match a report's own sweep batch (one undo
 *  transaction stays short); senders are the route's MAX_REPORT_SENDERS. */
const UNDO_CHUNK = 5000;
const UNDO_BLOCKED_CHUNK = 500;
/** The remove route's per-request limit. */
const REMOVE_CHUNK = 200;

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

export function useSpamSettings(accountId: string | undefined, enabled = true) {
  return useQuery<MailboxSpamSettingsDto>({
    queryKey: mb.spamSettings(accountId ?? ''),
    queryFn: () => api.getEmailMailboxSpamSettings(accountId as string),
    enabled: enabled && !!accountId,
  });
}

/** What the AI spam filter has cost this user (credits) by period — user-wide. */
export function useSpamSpend(enabled = true) {
  return useQuery<EmailSpamSpendDto>({
    queryKey: mb.spamSpend(),
    queryFn: () => api.getEmailMailboxSpamSpend(),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetSpamSettings(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      enabled?: boolean;
      rules?: string | null;
      spamRetention?: MailboxSpamRetentionDto;
    }) => api.setEmailMailboxSpamSettings(accountId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mb.spamSettings(accountId) });
      qc.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/** Rows per page of the allow/block lists. */
export const SPAM_SENDER_PAGE_SIZE = 50;

/**
 * The allow/block list, paged ("Load more") — one mailbox or every mailbox
 * (accountId omitted), optionally searched, with the total for the tab count.
 */
export function useSpamSenderPages(
  params: {
    disposition: 'approved' | 'rejected';
    accountId?: string;
    decidedBy?: 'user' | 'automated';
    q?: string;
  },
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: mb.spamSenderPages(params),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.listEmailSpamSenderPage({ ...params, limit: SPAM_SENDER_PAGE_SIZE, offset: pageParam }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.groups.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    enabled,
  });
}

/** Allow or block a sender by hand. A user decision: it replaces any existing
 *  entry, so allowing a blocked sender unblocks them (and vice versa). */
export function useAddSpamSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      senderAddress: string;
      disposition: 'approved' | 'rejected';
      accountId?: string;
      allMailboxes?: boolean;
    }) => api.addEmailSpamSender(input),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: mb.all });
      toast({
        description: `${vars.disposition === 'approved' ? 'Allowed' : 'Blocked'} ${vars.senderAddress.trim()}${
          vars.allMailboxes ? ' in all your mailboxes' : ''
        }`,
      });
    },
    onError: (e) =>
      toast({
        variant: 'destructive',
        description: e instanceof Error && e.message ? e.message : 'Could not update that sender',
      }),
  });
}

/**
 * After a block is removed (or a sender marked "not spam"), offer to bring back
 * the mail an earlier report sent to Trash — the durable recovery path once the
 * report's own Undo toast is gone. Lands in the Inbox (the original folder is
 * only known to that Undo).
 */
/** Restore until the server stops reporting `capped` (it moves ≤5000 per call). */
async function restoreAll(senders: { accountId: string; senderAddress: string }[]) {
  let restored = 0;
  for (let round = 0; round < MAX_REPORT_ROUNDS; round += 1) {
    const r = await api.restoreEmailSpamSenders(senders);
    restored += r.restored;
    if (!r.capped) break;
  }
  return restored;
}

function offerRestore(trashed: EmailSenderTrashedMail[], lead: string, refresh: () => void): void {
  const total = trashed.reduce((n, s) => n + s.count, 0);
  if (total === 0) {
    toast({ description: lead });
    return;
  }
  const who = trashed.length === 1 ? trashed[0].senderAddress : `${trashed.length} senders`;
  toast({
    description: `${lead} ${plural(total, 'message')} from ${who} ${total === 1 ? 'is' : 'are'} in Trash.`,
    action: (
      <ToastAction
        altText="Restore"
        onClick={() =>
          void restoreAll(
            trashed.map((s) => ({ accountId: s.accountId, senderAddress: s.senderAddress })),
          )
            .then((restored) =>
              toast({ description: `Restored ${plural(restored, 'message')} to your Inbox` }),
            )
            .catch((e) =>
              toast({
                variant: 'destructive',
                description: e instanceof Error && e.message ? e.message : 'Could not restore',
              }),
            )
            .finally(refresh)
        }
      >
        Restore
      </ToastAction>
    ),
  });
}

/** Remove several allow/block entries at once. */
export function useRemoveSpamSenders() {
  const qc = useQueryClient();
  return useMutation({
    // The route takes ≤200 entries per request; a selection can span more.
    mutationFn: async (entries: { screenedId: string; accountId: string }[]) => {
      let removed = 0;
      const trashed: EmailSenderTrashedMail[] = [];
      for (let i = 0; i < entries.length; i += REMOVE_CHUNK) {
        const r = await api.removeEmailSpamSenders(entries.slice(i, i + REMOVE_CHUNK));
        removed += r.removed;
        trashed.push(...(r.trashed ?? []));
      }
      return { removed, trashed };
    },
    onSuccess: (res) => {
      const refresh = () => void qc.invalidateQueries({ queryKey: mb.all });
      refresh();
      offerRestore(
        res.trashed ?? [],
        `Removed ${plural(res.removed, 'entry', 'entries')}.`,
        refresh,
      );
    },
    onError: (e) =>
      toast({
        variant: 'destructive',
        description: e instanceof Error && e.message ? e.message : 'Could not remove those senders',
      }),
  });
}

/** Trusted-without-being-listed counts (emailed addresses; contacts if on). */
export function useSpamTrustSummary(accountId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: mb.spamTrustSummary(accountId),
    queryFn: () => api.getEmailSpamTrustSummary(accountId),
    enabled,
    staleTime: 60_000,
  });
}

/** One entry per address, reasons unioned and filed counts summed — previews of
 *  several targets can each name the same sender. */
export function mergeKnownSenders(list: EmailKnownSender[]): EmailKnownSender[] {
  const byAddress = new Map<string, EmailKnownSender>();
  for (const k of list) {
    const cur = byAddress.get(k.address);
    if (!cur) {
      byAddress.set(k.address, { ...k, reasons: [...k.reasons] });
      continue;
    }
    for (const r of k.reasons) if (!cur.reasons.includes(r)) cur.reasons.push(r);
    if (k.filedCount) cur.filedCount = (cur.filedCount ?? 0) + k.filedCount;
  }
  // The server's fixed order, so the wording reads the same however it merged.
  const order: EmailKnownSender['reasons'] = ['emailed', 'contact', 'allowed', 'filed'];
  return [...byAddress.values()].map((k) => ({
    ...k,
    reasons: order.filter((r) => k.reasons.includes(r)),
  }));
}

/** Why a known sender is worth a second look, e.g. "you've emailed them · 14
 *  of their messages are in your folders". */
export function describeKnownSender(k: EmailKnownSender): string {
  return k.reasons
    .map((r) => {
      switch (r) {
        case 'emailed':
          return "you've emailed them";
        case 'contact':
          return 'in your contacts';
        case 'allowed':
          return 'on your allowed list';
        case 'filed': {
          const n = k.filedCount ?? 0;
          return n > 0
            ? `${plural(n, 'message')} from them ${n === 1 ? 'is' : 'are'} in your folders`
            : 'you kept their mail in your folders';
        }
      }
    })
    .join(' · ');
}

/**
 * The follow-up calls of a capped report: the senders the server resolved, one
 * call per mailbox (already widened, so `allMailboxes: false`). Older servers
 * don't send `remaining` — repeat the same target, as they expect.
 */
function continuations(
  r: EmailSpamReportResult,
  target: EmailSpamReportTarget,
): EmailSpamReportTarget[] {
  if (!r.remaining) return [{ ...target, checkKnownSenders: undefined }];
  return r.remaining.map((g) => ({
    accountId: g.accountId,
    senderAddresses: g.senderAddresses,
    allMailboxes: false,
  }));
}

/**
 * Report spam: block the target's senders and move ALL of their mail — every
 * folder, not just the selection — to Trash. Each target repeats while the
 * server reports `capped` (it sweeps at most 5000 rows per call), every
 * outcome is awaited, and ONE toast reports the result with an Undo that
 * moves each message back to its own folder and restores the senders'
 * previous dispositions. Deciding whether to confirm first (several senders, or
 * a sender the user knows) is the caller's job — see previewEmailSpamReport.
 */
export function useSpamReport() {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);

  const refresh = useCallback(() => {
    // Thousands of rows may have moved across folders, and the blocklist changed.
    void qc.invalidateQueries({ queryKey: mb.all });
    void qc.invalidateQueries({ queryKey: conn.all });
  }, [qc]);

  const undo = useCallback(
    async (moved: EmailSpamMovedMessage[], blocked: EmailSpamBlockedSender[]) => {
      try {
        let restored = 0;
        let unblocked = 0;
        // Both lists have per-request limits (a report across mailboxes can
        // block more entries than one request takes), so chunk each.
        const rounds = Math.max(
          Math.ceil(moved.length / UNDO_CHUNK),
          Math.ceil(blocked.length / UNDO_BLOCKED_CHUNK),
          1,
        );
        for (let i = 0; i < rounds; i += 1) {
          const r = await api.undoEmailSpamReport({
            moved: moved.slice(i * UNDO_CHUNK, (i + 1) * UNDO_CHUNK),
            blocked: blocked.slice(i * UNDO_BLOCKED_CHUNK, (i + 1) * UNDO_BLOCKED_CHUNK),
          });
          restored += r.restored;
          unblocked += r.unblocked;
        }
        toast({
          description: `Restored ${plural(restored, 'message')} and unblocked ${plural(unblocked, 'sender')}`,
        });
      } catch (e) {
        toast({
          variant: 'destructive',
          description: e instanceof Error && e.message ? e.message : 'Could not undo the report',
        });
      } finally {
        refresh();
      }
    },
    [refresh],
  );

  const run = useCallback(
    async (
      targets: EmailSpamReportTarget[],
      opts: { checkKnownSenders?: boolean } = {},
    ): Promise<{ needsConfirm?: true }> => {
      setRunning(true);
      const moved: EmailSpamMovedMessage[] = [];
      const blocked: EmailSpamBlockedSender[] = [];
      const senders = new Set<string>();
      let senderCount = 0;
      let error: string | null = null;
      let stillCapped = false;
      try {
        for (const [i, target] of targets.entries()) {
          // A capped report continues with the senders it resolved — the
          // original scope may no longer find them once they're blocked.
          const queue: EmailSpamReportTarget[] = [
            i === 0 && opts.checkKnownSenders ? { ...target, checkKnownSenders: true } : target,
          ];
          let rounds = 0;
          while (queue.length > 0 && rounds < MAX_REPORT_ROUNDS) {
            const next = queue.shift() as EmailSpamReportTarget;
            rounds += 1;
            let r: EmailSpamReportResult;
            try {
              r = await api.reportEmailMailboxSpam(next);
            } catch (e) {
              // Nothing changed: the server wants the known-sender confirm first.
              if (e instanceof ApiError && e.code === 'KNOWN_SENDERS')
                return { needsConfirm: true };
              throw e;
            }
            if (rounds === 1) senderCount += r.senderCount;
            r.senders?.forEach((a) => senders.add(a));
            moved.push(...r.moved);
            blocked.push(...r.blocked);
            if (r.error) {
              error = r.error;
              break;
            }
            if (r.capped) queue.push(...continuations(r, next));
          }
          if (queue.length > 0) stillCapped = true;
          if (error) break;
        }
      } catch (e) {
        error = e instanceof Error && e.message ? e.message : 'Could not report spam';
      } finally {
        setRunning(false);
        refresh();
      }
      if (error && moved.length === 0 && blocked.length === 0) {
        toast({ variant: 'destructive', description: error });
        return {};
      }
      // Distinct across targets when the server lists them (the same sender in
      // two mailboxes is one sender); older servers only send per-call counts.
      const count = senders.size > 0 ? senders.size : senderCount;
      const named = senders.size > 0 ? senders : new Set(blocked.map((b) => b.senderAddress));
      const who = count === 1 && named.size === 1 ? [...named][0] : plural(count, 'sender');
      const summary =
        `Reported ${who} as spam — moved ${plural(moved.length, 'message')} to Trash` +
        (stillCapped ? ' — more remain, report again to finish' : '') +
        (error ? ` — then stopped: ${error}` : '');
      toast({
        variant: error ? 'destructive' : undefined,
        description: summary,
        action:
          moved.length > 0 || blocked.length > 0 ? (
            <ToastAction altText="Undo" onClick={() => void undo(moved, blocked)}>
              Undo
            </ToastAction>
          ) : undefined,
      });
      return {};
    },
    [refresh, undo],
  );

  return { run, running };
}

/** Mark a Spam message as not spam: whitelist the sender + restore to Inbox. */
export function useNotSpam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId }: { accountId: string; messageId: string }) =>
      api.notEmailMailboxSpam(messageId),
    onSuccess: (res) => {
      const refresh = () => void qc.invalidateQueries({ queryKey: mb.all });
      refresh();
      offerRestore(
        res.trashed ?? [],
        `Marked not spam — ${plural(res.moved, 'message')} moved to Inbox.`,
        refresh,
      );
    },
  });
}
